/**
 * Ramuza Atena II Scale - Web Serial API Parser
 * 
 * The Ramuza scale sends weight data as a string via serial port.
 * Typical format: "ST,GS,+  0.350kg\r\n" or "ST,GS,+000.350kg\r\n"
 * Where:
 *   ST = Stable reading
 *   US = Unstable reading
 *   GS = Gross weight
 *   NT = Net weight
 *   + / - = Sign
 *   Followed by the weight value and unit
 * 
 * Serial settings for Ramuza Atena II:
 *   Baud rate: 9600
 *   Data bits: 8
 *   Stop bits: 1
 *   Parity: none
 */

export function parseRamuzaWeight(rawString) {
  if (!rawString || typeof rawString !== 'string') {
    return { valid: false, weight: 0, unit: 'kg', stable: false, raw: rawString };
  }

  const cleaned = rawString.trim();

  // Try to extract weight value from Ramuza format
  // Pattern: optional status, then a number with optional decimal, then unit
  const weightRegex = /([+-]?)\s*(\d+\.?\d*)\s*(kg|g|lb)/i;
  const match = cleaned.match(weightRegex);

  if (!match) {
    return { valid: false, weight: 0, unit: 'kg', stable: false, raw: cleaned };
  }

  const sign = match[1] === '-' ? -1 : 1;
  const value = parseFloat(match[2]) * sign;
  const unit = match[3].toLowerCase();
  const stable = cleaned.toUpperCase().includes('ST');

  // Convert to kg if needed
  let weightKg = value;
  if (unit === 'g') {
    weightKg = value / 1000;
  }

  return {
    valid: true,
    weight: weightKg,
    weightRaw: value,
    unit,
    stable,
    raw: cleaned,
  };
}

export async function connectScale() {
  if (!('serial' in navigator)) {
    throw new Error('Web Serial API nao suportada neste navegador. Use Chrome/Edge.');
  }

  const port = await navigator.serial.requestPort();
  await port.open({
    baudRate: 9600,
    dataBits: 8,
    stopBits: 1,
    parity: 'none',
  });

  return port;
}

export function createScaleReader(port, onWeight) {
  let buffer = '';
  const decoder = new TextDecoderStream();
  const readableStreamClosed = port.readable.pipeTo(decoder.writable);
  const reader = decoder.readable.getReader();
  let running = true;

  async function readLoop() {
    while (running) {
      try {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += value;
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.trim()) {
            const parsed = parseRamuzaWeight(line);
            if (parsed.valid) {
              onWeight(parsed);
            }
          }
        }
      } catch (err) {
        if (running) {
          console.error('Erro leitura serial:', err);
        }
        break;
      }
    }
  }

  readLoop();

  return {
    stop: async () => {
      running = false;
      try {
        reader.cancel();
        await readableStreamClosed.catch(() => {});
        await port.close();
      } catch (e) {
        // port already closed
      }
    },
  };
}

// Simulate scale data for testing
export function simulateScaleReading() {
  const weights = [0.35, 0.5, 0.75, 1.0, 1.25, 0.15, 0.8, 2.0];
  const weight = weights[Math.floor(Math.random() * weights.length)];
  const raw = `ST,GS,+  ${weight.toFixed(3)}kg\r\n`;
  return parseRamuzaWeight(raw);
}


// ─── Fase 28 — Cliente Ethernet via bridge local (WebSocket) ────────────
// Browser não abre TCP direto (sandbox). O usuário roda o script
// `kaven-scale-bridge.js` no PC da loja que faz a ponte TCP ↔ WebSocket.

export function connectScaleEthernet({ url = 'ws://localhost:8765', onMessage, onConnectionChange } = {}) {
  if (typeof WebSocket === 'undefined') throw new Error('WebSocket não suportado');
  let ws = null;
  let closed = false;
  let reconnectTimer = null;

  const scheduleReconnect = () => {
    if (closed || reconnectTimer) return;
    reconnectTimer = setTimeout(() => { reconnectTimer = null; connect(); }, 3000);
  };

  const connect = () => {
    if (closed) return;
    try { ws = new WebSocket(url); }
    catch { onConnectionChange?.(false); scheduleReconnect(); return; }
    ws.onopen = () => {
      // eslint-disable-next-line no-console
      console.log('[SCALE BRIDGE] conectada:', url);
      onConnectionChange?.(true);
    };
    ws.onmessage = (ev) => { try { onMessage?.(JSON.parse(ev.data)); } catch {} };
    ws.onclose = () => { onConnectionChange?.(false); scheduleReconnect(); };
    ws.onerror = () => {};
  };

  connect();

  return {
    close: () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws) try { ws.close(); } catch {}
    },
    isOpen: () => ws && ws.readyState === WebSocket.OPEN,
  };
}

export async function probeScaleBridge(url = 'http://localhost:8765/health') {
  try {
    const res = await fetch(url, { method: 'GET', cache: 'no-store' });
    if (!res.ok) return { ok: false };
    return { ok: true, ...(await res.json()) };
  } catch {
    return { ok: false };
  }
}
